import { useState, useEffect, useCallback } from "react";
import recipeService from "@/services/reciepeService";
import { toast } from "react-hot-toast";

export const useRecipe = () => {
  const [inventoryItems, setInventoryItems] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [masterLoading, setMasterLoading] = useState(false);

  const [recipes, setRecipes] = useState([]);
  const [currentRecipe, setCurrentRecipe] = useState(null);
  const [recipesLoading, setRecipesLoading] = useState(false);

  // ----------------------------
  // MAPPERS
  // ----------------------------

  /** Inventory Mapper */
const mapInventoryFromApi = (data = {}) => {
  const uomEntity = data.idUom ?? data.uom ?? null;
  let uomString = '';
  let uomId = null;
  let uomCategoryCode = null;
  let uomConversionFactor = 1;

  if (uomEntity) {
    if (typeof uomEntity === 'string') {
      uomString = uomEntity;
    } else if (typeof uomEntity === 'object') {
      uomString = uomEntity.symbol ?? uomEntity.name ?? '';
      uomId = uomEntity.id ?? null;
      // Extract UoM category code for filtering compatible UoMs
      // Handle nested object: uomCategory: { id, code, name }
      const uomCat = uomEntity.uomCategory ?? uomEntity.category ?? null;
      uomCategoryCode = typeof uomCat === 'object' && uomCat !== null
        ? uomCat.code
        : uomCat;
      // Extract conversion factor
      uomConversionFactor = uomEntity.conversionFactor ?? 1;
    }
  }

  const categoryEntity = data.idCategory ?? data.category ?? null;
  const categoryName =
    typeof categoryEntity === 'string'
      ? categoryEntity
      : typeof categoryEntity === 'object' && categoryEntity !== null
      ? categoryEntity.name ?? ''
      : '';

  return {
    id: data.id,
    name: data.name ?? data.itemName ?? '',
    uom: uomString,
    uomId,
    uomCategory: uomCategoryCode,        // UoM category code for filtering (WEIGHT, VOLUME, COUNT, LENGTH)
    uomConversionFactor,                 // Conversion factor of inventory's UoM
    unitCost: data.unitCost ?? data.unit_cost ?? data.cost ?? 0,
    category: categoryName,
  };
};




  /** UOM Mapper - includes category for filtering */
const mapUomFromApi = (data = {}) => {
  // if backend sometimes sends plain strings
  if (typeof data === "string") {
    return {
      id: data,
      label: data,
      uomCategory: null,
      conversionFactor: 1,
    };
  }

  // Extract category code from nested object: uomCategory: { id, code, name }
  const uomCat = data.uomCategory ?? data.category ?? null;
  const uomCategoryCode = typeof uomCat === 'object' && uomCat !== null
    ? uomCat.code
    : uomCat;

  return {
    id:
      data.id ??
      data.idUom ??
      data.code ??
      data.symbol, // fallback; adjust if needed
    label:
      data.symbol ??   // "gm"
      data.code ??
      data.name ??
      data.uom ??
      data.label ??
      "",
    // Include category code for filtering compatible UoMs (WEIGHT, VOLUME, etc.)
    uomCategory: uomCategoryCode,
    conversionFactor: data.conversionFactor ?? 1,
    isBaseUnit: data.isBaseUnit ?? false,
  };
};


  /** Recipe → API */

/** Recipe → API (for create/update) */
const mapRecipeToApi = (data = {}) => {
  const isUpdate = !!data.id; // recipe id present → update

  // MENU ITEM
  const menuItemId = data.menuItemId ?? data.idMenuItem ?? null;

  // VARIANT
  const variantId = data.variantId ?? data.idVariant ?? null;

  // Base recipe DTO
  const recipeDto = {
    id: isUpdate ? data.id : null,

    menuItem: menuItemId
      ? {
          id: menuItemId,
          name: data.menuItemName ?? undefined,
          basePrice: data.basePrice ?? data.sellingPrice ?? undefined,
        }
      : null,

    variant: variantId
      ? {
          id: variantId,
          menuItemId: menuItemId,
          variantName: data.variantName ?? undefined,
          variantPrice: data.variantPrice ?? data.sellingPrice ?? undefined,
        }
      : null,

    notes: data.prepNotes ?? data.notes ?? "",
    isActive: data.isActive ?? true,
  };

  // INGREDIENTS
  const ingredients =
    (data.ingredients ?? [])
      .map((ing) => {
        // inventory item id
        const inventoryId =
          ing.idInventoryItem ??     // when you add from editor: you set this
          ing.inventoryItemId ??    // alternative name
          ing.id ??                 // from mapRecipeFromApi: id = invId
          null;

        // uom id
        const uomId =
          ing.idUom ??
          ing.uomId ??
          null;

        // quantity
        const quantity =
          ing.quantity !== "" && ing.quantity != null
            ? Number(ing.quantity)
            : 0;

        // 🚫 If we don't have inventoryId or uomId, skip this ingredient
        if (!inventoryId || !uomId) {
          console.warn("Skipping invalid ingredient in mapRecipeToApi:", ing);
          return null;
        }

        const dto = {
          quantity,
          idInventoryItem: { id: inventoryId },
          idUom: { id: uomId },
        };

        // For UPDATE, send row id only for existing ingredients
        if (isUpdate && ing.ingredientRowId) {
          dto.id = ing.ingredientRowId;
        }

        return dto;
      })
      .filter(Boolean); // remove nulls

  return {
    ...recipeDto,
    ingredients,
  };
};



/** Recipe ← API */
const mapRecipeFromApi = (data = {}) => ({
  id: data.id,

  // -----------------------
  // MENU ITEM
  // -----------------------
  menuItemId:
    data.menuItemId ??
    data.idMenuItem ??
    data.menuItem?.id ??
    null,

  menuItemName:
    data.menuItemName ??
    data.menuItem?.name ??
    `Menu #${data.idMenuItem ?? data.menuItem?.id ?? ''}`,

  categoryId:
    data.categoryId ??
    data.menuItem?.categoryId ??
    data.menuItem?.category?.id ??
    null,

  categoryName:
    data.categoryName ??
    data.menuItem?.categoryName ??
    data.menuItem?.category?.name ??
    '',

  // -----------------------
  // VARIANT
  // -----------------------
  variantId:
    data.variantId ??
    data.idVariant ??
    data.variant?.id ??
    null,

  variantName:
    data.variantName ??
    data.variant?.variantName ??
    (data.idVariant || data.variant?.id
      ? `Variant #${data.idVariant ?? data.variant?.id}`
      : ''),

  // -----------------------
  // PRICES
  // -----------------------
  basePrice:
    data.basePrice ??
    data.menuItem?.basePrice ??
    0,

  sellingPrice:
    data.sellingPrice ??
    data.variant?.variantPrice ??
    data.menuItem?.basePrice ??
    0,

  totalCost: data.totalCost ?? 0,

  // -----------------------
  // NOTES & TIMESTAMPS
  // -----------------------
  prepNotes: data.prepNotes ?? data.notes ?? '',
  createdAt: data.createdAt ?? data.created_at ?? null,
  updatedAt: data.updatedAt ?? data.updated_at ?? null,

  // -----------------------
  // INGREDIENTS (FLATTEN) - with UoM conversion
  // -----------------------
  ingredients:
    (data.ingredients ?? []).map((ing) => {
      // inventory item object
      const inv =
        ing.idInventoryItem ??
        ing.inventoryItem ??
        null;

      const invId =
        typeof inv === 'object' && inv !== null
          ? inv.id
          : inv;

      const invName =
        (typeof inv === 'object' && inv !== null &&
          (inv.name ?? inv.itemName)) ??
        ing.inventoryItemName ??
        ing.name ??
        `Item #${invId ?? ''}`;

      // Recipe ingredient's UOM (what user selected in recipe)
      const recipeUomEntity =
        ing.idUom ??
        ing.uom ??
        null;

      const recipeUomId =
        typeof recipeUomEntity === 'object' && recipeUomEntity !== null
          ? recipeUomEntity.id
          : recipeUomEntity ?? null;

      const recipeUomLabel =
        (typeof recipeUomEntity === 'object' && recipeUomEntity !== null &&
          (recipeUomEntity.symbol ?? recipeUomEntity.name)) ||
        '';

      // Recipe UoM conversion factor (e.g., gram = 1, kg = 1000)
      const recipeUomFactor =
        (typeof recipeUomEntity === 'object' && recipeUomEntity !== null
          ? recipeUomEntity.conversionFactor
          : null) ?? 1;

      // Inventory item's UOM (how it's stored in inventory)
      const invUomEntity =
        typeof inv === 'object' && inv !== null ? inv.idUom : null;

      // Inventory UoM conversion factor
      const invUomFactor =
        (typeof invUomEntity === 'object' && invUomEntity !== null
          ? invUomEntity.conversionFactor
          : null) ?? 1;

      // Inventory unit cost (cost per inventory UoM, e.g., ₹50/kg)
      const inventoryUnitCost =
        ing.unitCost ??
        (typeof inv === 'object' && inv !== null ? inv.unitCost : 0) ??
        0;

      // Calculate converted unit cost based on UoM conversion
      // Formula: convertedUnitCost = inventoryUnitCost * (recipeUomFactor / invUomFactor)
      // Example: ₹50/kg * (1/1000) = ₹0.05/g
      const convertedUnitCost = invUomFactor > 0
        ? inventoryUnitCost * (recipeUomFactor / invUomFactor)
        : inventoryUnitCost;

      const quantity = ing.quantity ?? 0;
      const totalCost = quantity * convertedUnitCost;

      return {
        // inventory item id (important: used in editor)
        id: invId,

        // 🔥 join-table primary key (backend ingredient row id)
        ingredientRowId: ing.id,

        name: invName,
        quantity,
        idUom: recipeUomId,
        uom: recipeUomLabel,
        // Conversion data
        inventoryUomFactor: invUomFactor,
        recipeUomFactor: recipeUomFactor,
        inventoryUnitCost: inventoryUnitCost,
        // Use converted unit cost for display
        unitCost: convertedUnitCost,
        totalCost,
      };
    }) ?? [],
});



  // ----------------------------
  // LOAD MASTER DATA (inventory + uom)
  // ----------------------------

  const fetchRecipeMasterData = useCallback(async () => {
    setMasterLoading(true);

    try {
      const [itemsRes, uomsRes] = await Promise.all([
        recipeService.getInventoryItems(),
        recipeService.getUoms(),
      ]);

      const rawItems = Array.isArray(itemsRes?.data?.data)
        ? itemsRes.data.data
        : itemsRes.data ?? itemsRes;

      const rawUoms = Array.isArray(uomsRes?.data?.data)
        ? uomsRes.data.data
        : uomsRes.data ?? uomsRes;

      setInventoryItems(rawItems.map(mapInventoryFromApi));
      setUoms(rawUoms.map(mapUomFromApi));
    } catch (e) {
      toast.error("Failed loading inventory & UOM");
      setInventoryItems([]);
      setUoms([]);
    } finally {
      setMasterLoading(false);
    }
  }, []);

  // ----------------------------
  // RECIPE CRUD
  // ----------------------------

// useReciepe.js
const fetchRecipes = useCallback(async (branchId) => {
  setRecipesLoading(true);

  try {
    const filters = {};
    if (branchId) filters.branchId = branchId;
    const res = await recipeService.getAll(filters);

    // Handle both plain array and Spring pageable response
    const page = res?.data ?? res;

    console.log(" /api/admin/recipe raw page:", page);

    // Support both plain array and pageable { content: [] } formats
    const list = Array.isArray(page)
      ? page
      : Array.isArray(page.content)
        ? page.content
        : [];

    console.log(" recipe rows before mapping:", list);

    const mapped = list.map(mapRecipeFromApi);

    console.log(" mapped recipes:", mapped);

    setRecipes(mapped);
  } catch (e) {
    console.error(" Failed loading recipes:", e);
    toast.error("Failed loading recipes");
    setRecipes([]);
  } finally {
    setRecipesLoading(false);
  }
}, []);



 // CREATE
const createRecipe = async (data) => {
  //  don't pass any `id` here
  const payload = mapRecipeToApi({ ...data, id: null });

  console.log("create payload:", payload); // check ingredients[].id are null or missing
  const res = await recipeService.create(payload);
  // toast.success("Recipe created");
  await fetchRecipes();
};

// UPDATE
const updateRecipe = async (id, data) => {
  const payload = mapRecipeToApi({ ...data, id });
  console.log(" update payload:", payload);
console.log("Editor ingredients:", data.ingredients);
console.log("Payload:", JSON.stringify(mapRecipeToApi({ ...data, id }), null, 2));

  await recipeService.update(id, payload);  // 🔹 uses PUT /api/admin/recipe/{id}
  // toast.success("Recipe updated");
  await fetchRecipes();
};


  const deleteRecipe = async (id) => {
    await recipeService.delete(id);
    // toast.success("Recipe deleted");
    setRecipes((p) => p.filter((r) => r.id !== id));
  };

  // ----------------------------
  // INGREDIENT CRUD
  // ----------------------------
// maps one UI ingredient object -> API ingredient body for PATCH
const mapSingleIngredientToApi = (recipeId, ing) => {
  const tmpRecipe = mapRecipeToApi({
    id: recipeId,
    ingredients: [ing],
  });

  // mapRecipeToApi returns { ingredients: [ { ... } ] }
  return (tmpRecipe.ingredients && tmpRecipe.ingredients[0]) || null;
};

const updateRecipeIngredients = async (recipeId, ingredient) => {
  // ingredient: the single ingredient from UI
  const payload = mapSingleIngredientToApi(recipeId, ingredient);

  if (!payload) {
    throw new Error("Invalid ingredient payload");
  }

  await recipeService.updateIngredients(recipeId, payload);
  toast.success("Ingredient saved");
};


  const deleteRecipeIngredient = async (recipeId, ingredientId) => {
    await recipeService.deleteIngredient(recipeId, ingredientId);
    toast.success("Ingredient removed");
  };

  // ----------------------------
  // AUTO LOAD MASTER
  // ----------------------------

  useEffect(() => {
    fetchRecipeMasterData();
  }, [fetchRecipeMasterData]);

  return {
    inventoryItems,
    uoms,
    masterLoading,

    recipes,
    currentRecipe,
    recipesLoading,

    fetchRecipes,
    createRecipe,
    updateRecipe,
    deleteRecipe,
    updateRecipeIngredients,
    deleteRecipeIngredient,
  };
};

export default useRecipe;
